import { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { sandboxApi, type SandboxTraceStatus } from '@/api/sandbox.api';
import { useCursorPager } from '@/hooks/useCursorPager';
import { formatTimestamp } from '@/utils/utils';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { TraceIcon, EmptyBoxIcon, ErrorCircleIcon } from './shared';

const TIME_RANGES = [
  { label: '过去 3 小时', hours: 3 },
  { label: '过去 6 小时', hours: 6 },
  { label: '过去 12 小时', hours: 12 },
  { label: '过去 1 天', hours: 24 },
  { label: '过去 7 天', hours: 168 },
  { label: '过去 30 天', hours: 720 },
];

function rangeToTimestamps(hours: number | null, now: number): { from?: string; to?: string } {
  if (hours === null) return {};
  return { from: new Date(now - hours * 60 * 60 * 1000).toISOString(), to: new Date(now).toISOString() };
}

const statusConfig: Record<
  SandboxTraceStatus,
  { label: string; dot: string; badge: 'default' | 'secondary' | 'outline' | 'destructive' }
> = {
  RUNNING: { label: '运行中', dot: 'bg-blue-500 animate-pulse', badge: 'default' },
  FINISHED: { label: '已完成', dot: 'bg-emerald-500', badge: 'secondary' },
  FAILED: { label: '失败', dot: 'bg-red-500', badge: 'destructive' },
  UNKNOWN: { label: '未知', dot: 'bg-gray-400', badge: 'outline' },
};

export default function SandboxTraces() {
  const pager = useCursorPager();
  const [pageSize, setPageSize] = useState(10);
  const [timeRange, setTimeRange] = useState<number | null>(24);
  const [selectedInstanceId, setSelectedInstanceId] = useState('');

  // 条件变化时更新基准时间，翻页时保持不变
  const [windowAnchor, setWindowAnchor] = useState(() => Date.now());
  const timestamps = useMemo(() => rangeToTimestamps(timeRange, windowAnchor), [timeRange, windowAnchor]);

  const {
    data: traceRecordsData,
    isLoading: traceRecordsLoading,
    error: traceRecordsError,
    refetch: traceRecordsRefetch,
  } = useQuery({
    queryKey: [
      'sandbox-trace-records',
      pager.cursor,
      pageSize,
      timestamps.from,
      timestamps.to,
      selectedInstanceId,
    ],
    queryFn: () =>
      sandboxApi.listSandboxTraceRecords({
        pageSize,
        cursor: pager.cursor,
        withTotal: pager.isFirstPage,
        fromTimestamp: timestamps.from,
        toTimestamp: timestamps.to,
        sandboxId: selectedInstanceId || undefined,
      }),
  });

  const { setTotal } = pager;
  useEffect(() => {
    if (traceRecordsData?.total !== undefined) setTotal(traceRecordsData.total);
  }, [traceRecordsData?.total, setTotal]);

  const { data: instancesData } = useQuery({
    queryKey: ['sandbox-instances-for-filter'],
    queryFn: () => sandboxApi.listSandboxes(create(ListRequestSchema, { page: 1, pageSize: 200 })),
  });

  const instances = (instancesData?.data || []).filter(Boolean);
  const selectedInstance = instances.find((inst) => inst.id === selectedInstanceId);
  const traceRecords = traceRecordsData?.items || [];

  return (
    <>
      {/* 不用 overflow-hidden：实例下拉面板为绝对定位，列表较矮时会被裁剪 */}
      <div className="bg-background rounded-xl border shadow-sm">
        <div className="px-5 py-4 border-b space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <TraceIcon />
              <span className="text-sm font-medium text-foreground">沙箱运行记录</span>
              {!traceRecordsLoading && traceRecords.length > 0 && pager.total !== undefined && (
                <span className="text-xs text-muted-foreground ml-1">约 {pager.total} 条记录</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground shrink-0">时间范围</span>
              <select
                value={timeRange ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setTimeRange(v ? Number(v) : null);
                  setWindowAnchor(Date.now());
                  pager.reset();
                }}
                className="h-8 rounded-md border border-input bg-background px-2.5 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
              >
                <option value="">全部</option>
                {TIME_RANGES.map((r) => (
                  <option key={r.hours} value={r.hours}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground shrink-0">实例</span>
              {/* 与 Failover 配置「获取模型列表」的下拉同款：面板内置搜索框，按实例名称或 ID 检索。 */}
              <div className="w-[260px]">
                <Select
                  value={selectedInstanceId}
                  onValueChange={(value) => {
                    setSelectedInstanceId(value);
                    setWindowAnchor(Date.now());
                    pager.reset();
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="全部实例">
                      {selectedInstance ? selectedInstance.name : selectedInstanceId || undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent searchable searchPlaceholder="搜索实例名称或 ID...">
                    <SelectItem value="" searchKeywords="全部实例">
                      全部实例
                    </SelectItem>
                    {instances.map((inst) => (
                      <SelectItem
                        key={inst.id}
                        value={inst.id}
                        searchKeywords={`${inst.name} ${inst.id}`}
                      >
                        <span className="flex items-center justify-between w-full gap-2">
                          <span className="truncate">{inst.name}</span>
                          <span className="text-xs text-muted-foreground font-mono shrink-0">
                            {inst.id}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {traceRecordsLoading ? (
          <div className="p-5 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-lg border p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-4 w-28" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-3 w-4" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-2 w-full" />
              </div>
            ))}
          </div>
        ) : traceRecordsError ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <span className="text-destructive">
                <ErrorCircleIcon />
              </span>
            </div>
            <p className="font-semibold text-foreground mb-1">加载失败</p>
            <p className="text-sm text-muted-foreground mb-5">请检查网络连接后重试</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => traceRecordsRefetch()}
              className="gap-1.5"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              重试
            </Button>
          </div>
        ) : traceRecords.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <EmptyBoxIcon />
            <p className="font-semibold text-foreground mt-4 mb-1">
              {traceRecordsData?.hasMore ? '本页无可显示的记录' : '暂无运行记录'}
            </p>
            <p className="text-sm text-muted-foreground mb-5">
              {traceRecordsData?.hasMore ? '请翻到下一页继续查看' : '暂无沙箱操作运行记录'}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {traceRecords.map((record, idx) => {
              const status = statusConfig[record.status];
              const durationMs = record.durationMs;
              const durationLabel = durationMs > 0 ? `${(durationMs / 1000).toFixed(1)}s` : '-';

              return (
                <div
                  key={idx}
                  className="group rounded-lg border bg-card hover:border-primary/30 transition-colors"
                >
                  <div className="p-4 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${status.dot}`} />
                        <Badge variant={status.badge} className="text-[11px] px-2 py-0">
                          {status.label}
                        </Badge>
                      </div>
                      <span className="text-[11px] text-muted-foreground font-mono">
                        {record.sandboxId || '-'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground shrink-0">开始</span>
                      <span className="font-mono text-foreground/80">
                        {formatTimestamp(record.startTimeNs)}
                      </span>
                      <span className="text-muted-foreground/40 mx-1">→</span>
                      <span className="text-muted-foreground shrink-0">结束</span>
                      <span className="font-mono text-foreground/80">
                        {record.status === 'RUNNING' ? '运行中' : formatTimestamp(record.endTimeNs)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span>耗时</span>
                      <span className="font-mono font-medium text-foreground/70">
                        {durationLabel}
                      </span>
                    </div>

                    {record.status === 'FAILED' && record.statusMessage && (
                      <div className="flex items-center gap-2 text-[11px] text-destructive">
                        <span className="shrink-0">失败原因</span>
                        <span className="truncate">{record.statusMessage}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="shrink-0">Trace ID</span>
                      <span className="font-mono text-foreground/70 truncate">
                        {record.traceId || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!traceRecordsLoading &&
          !traceRecordsError &&
          (traceRecords.length > 0 || traceRecordsData?.hasMore || !pager.isFirstPage) && (
          <div className="flex items-center justify-between px-5 py-4 border-t bg-muted/10 gap-4 rounded-b-xl">
            <p className="text-sm text-muted-foreground">
              {pager.total !== undefined && (
                <>
                  约 <span className="font-medium text-foreground">{pager.total}</span> 条
                </>
              )}
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">每页</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setWindowAnchor(Date.now());
                    pager.reset();
                  }}
                  className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs text-muted-foreground">条</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={pager.isFirstPage}
                  onClick={pager.prev}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border text-xs">
                  <span className="font-medium">第 {pager.page} 页</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={!traceRecordsData?.hasMore}
                  onClick={() => pager.next(traceRecordsData?.nextCursor ?? '')}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
